import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { refreshGameData } from "../../../../State/actions";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

/**
 * HeartbeatManager - Manages periodic game state synchronization
 *
 * Features:
 * - Calls init API every 30 seconds to sync balance and detect free rounds
 * - Starts 30 seconds after game initialization
 * - Pauses during active spins to avoid interrupting gameplay
 * - Handles errors gracefully without blocking gameplay
 * - Tracks consecutive failures for error state management
 * - Automatically triggers free rounds popup if server grants free rounds
 * 
 * Can be disabled via FEATURES_CONFIG.HEARTBEAT.ENABLED
 */
const HeartbeatManager = () => {
  const dispatch = useDispatch();

  // Selectors
  const gameStatus = useSelector((state) => state.game.status);
  const assetsStatus = useSelector((state) => state.assets.status);

  // Refs to persist values across renders
  const intervalRef = useRef(null);
  const startDelayRef = useRef(null);
  const isRunningRef = useRef(false);

  // Check if game is ready to start heartbeat
  const isGameReady =
    assetsStatus === "succeeded" &&
    gameStatus === "succeeded";

  // Check if spin is in progress
  const isSpinning = gameStatus === "spinning";

  /**
   * Execute heartbeat sync
   * Skips if already running or if spin is active
   */
  const executeHeartbeat = useCallback(() => {
    // Skip if previous heartbeat still running
    if (isRunningRef.current) {
      return;
    }

    // Skip if spin is active (pause during gameplay)
    if (FEATURES_CONFIG.HEARTBEAT.PAUSE_DURING_SPIN && isSpinning) {
      return;
    }

    // Execute heartbeat - use refreshGameData to handle free rounds
    isRunningRef.current = true;
    dispatch(refreshGameData())
      .catch(() => {
        // Fail silently
      })
      .finally(() => {
        isRunningRef.current = false;
      });
  }, [dispatch, isSpinning]);

  /**
   * Start heartbeat interval
   */
  const startHeartbeat = useCallback(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start interval
    intervalRef.current = setInterval(() => {
      executeHeartbeat();
    }, FEATURES_CONFIG.HEARTBEAT.INTERVAL);
  }, [executeHeartbeat]);

  /**
   * Stop heartbeat interval
   */
  const stopHeartbeat = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Initialize heartbeat with delay
   */
  useEffect(() => {
    // Don't start heartbeat if disabled
    if (!FEATURES_CONFIG.HEARTBEAT.ENABLED) {
      return;
    }

    if (!isGameReady) {
      return;
    }

    // Start heartbeat after initial delay
    startDelayRef.current = setTimeout(() => {
      startHeartbeat();
    }, FEATURES_CONFIG.HEARTBEAT.START_DELAY);

    // Cleanup on unmount
    return () => {
      if (startDelayRef.current) {
        clearTimeout(startDelayRef.current);
      }
      stopHeartbeat();
    };
  }, [isGameReady, startHeartbeat, stopHeartbeat, assetsStatus, gameStatus]);

  // This component doesn't render anything
  return null;
};

export default React.memo(HeartbeatManager);
